

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Form,
  InputNumber,
  Row,
  Space,
  Switch,
  TimePicker,
  Typography,
  message,
} from "antd";
import {
  SaveOutlined,
  ReloadOutlined,
  BellOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

const { Title, Text } = Typography;

function getTelegramInitData() {
  try {
    return window?.Telegram?.WebApp?.initData || "";
  } catch {
    return "";
  }
}

async function apiFetch(path, { method = "GET", body, query } = {}) {
  const initData = getTelegramInitData();
  const qs =
    query && Object.keys(query).length
      ? "?" +
        Object.entries(query)
          .filter(([, v]) => v !== undefined && v !== null && String(v) !== "")
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join("&")
      : "";

  const res = await fetch(`/api${path}${qs}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(initData ? { "x-telegram-init-data": initData } : {}),
    },
    credentials: "include",
    body: body ? JSON.stringify(body) : undefined,
  });

  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }

  if (!res.ok) {
    const err = new Error(data?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

function toBool(v, def = false) {
  if (v == null) return def;
  if (typeof v === "boolean") return v;
  const s = String(v).trim().toLowerCase();
  return s === "1" || s === "true" || s === "yes" || s === "on";
}

function toNum(v, def = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
}

function hourToDayjs(v, defHour) {
  const n = Math.max(0, Math.min(23, toNum(v, defHour)));
  return dayjs().hour(n).minute(0).second(0).millisecond(0);
}

export default function Settings() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [rawSettings, setRawSettings] = useState({});

  const normalized = useMemo(() => {
    return {
      alerts_enabled: toBool(rawSettings.alerts_enabled, true),
      alerts_cooldown_min: toNum(rawSettings.alerts_cooldown_min, 30),
      alerts_max_repeats: toNum(rawSettings.alerts_max_repeats, 3),
      alerts_digest_enabled: toBool(rawSettings.alerts_digest_enabled, true),
      alerts_digest_interval_min: toNum(rawSettings.alerts_digest_interval_min, 10),
      alerts_only_on_status_change: toBool(rawSettings.alerts_only_on_status_change, true),
      alerts_group_threshold: toNum(rawSettings.alerts_group_threshold, 5),
      sla_default_min: toNum(rawSettings.sla_default_min, 15),
      alerts_quiet_hours_from: hourToDayjs(rawSettings.alerts_quiet_hours_from, 23),
      alerts_quiet_hours_to: hourToDayjs(rawSettings.alerts_quiet_hours_to, 8),
    };
  }, [rawSettings]);

  async function loadSettings() {
    setLoading(true);
    setError("");
    try {
      const data = await apiFetch("/settings");
      const settings = data?.settings && typeof data.settings === "object" ? data.settings : {};
      setRawSettings(settings);
      form.setFieldsValue({
        alerts_enabled: toBool(settings.alerts_enabled, true),
        alerts_cooldown_min: toNum(settings.alerts_cooldown_min, 30),
        alerts_max_repeats: toNum(settings.alerts_max_repeats, 3),
        alerts_digest_enabled: toBool(settings.alerts_digest_enabled, true),
        alerts_digest_interval_min: toNum(settings.alerts_digest_interval_min, 10),
        alerts_only_on_status_change: toBool(settings.alerts_only_on_status_change, true),
        alerts_group_threshold: toNum(settings.alerts_group_threshold, 5),
        sla_default_min: toNum(settings.sla_default_min, 15),
        alerts_quiet_hours_from: hourToDayjs(settings.alerts_quiet_hours_from, 23),
        alerts_quiet_hours_to: hourToDayjs(settings.alerts_quiet_hours_to, 8),
      });
    } catch (e) {
      setError(e?.message || "Не удалось загрузить настройки");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function saveSettings(values) {
    setSaving(true);
    setError("");
    try {
      const payload = {
        alerts_enabled: values.alerts_enabled ? "1" : "0",
        alerts_cooldown_min: String(toNum(values.alerts_cooldown_min, 30)),
        alerts_max_repeats: String(toNum(values.alerts_max_repeats, 3)),
        alerts_digest_enabled: values.alerts_digest_enabled ? "1" : "0",
        alerts_digest_interval_min: String(toNum(values.alerts_digest_interval_min, 10)),
        alerts_only_on_status_change: values.alerts_only_on_status_change ? "1" : "0",
        alerts_group_threshold: String(toNum(values.alerts_group_threshold, 5)),
        sla_default_min: String(toNum(values.sla_default_min, 15)),
        alerts_quiet_hours_from: String(values.alerts_quiet_hours_from?.hour?.() ?? 23),
        alerts_quiet_hours_to: String(values.alerts_quiet_hours_to?.hour?.() ?? 8),
      };

      const data = await apiFetch("/settings", {
        method: "POST",
        body: payload,
      });

      if (!data?.ok) throw new Error(data?.error || "save_failed");

      setRawSettings(payload);
      message.success("Настройки сохранены");
    } catch (e) {
      setError(e?.message || "Не удалось сохранить настройки");
      message.error("Не удалось сохранить настройки");
    } finally {
      setSaving(false);
    }
  }

  async function sendTestAlert() {
    try {
      message.loading({ content: "Отправляем тестовый алерт...", key: "settings-test-alert" });
      const data = await apiFetch("/settings/test-alert", { method: "POST", body: {} });
      if (!data?.ok) throw new Error(data?.error || "test_alert_failed");
      message.success({ content: "Тестовый алерт отправлен", key: "settings-test-alert" });
    } catch {
      message.error({ content: "Не удалось отправить тестовый алерт", key: "settings-test-alert" });
    }
  }

  async function resetAlertCounters() {
    try {
      message.loading({ content: "Сбрасываем счётчики алертов...", key: "settings-reset-alerts" });
      const data = await apiFetch("/settings/reset-alert-counters", { method: "POST", body: {} });
      if (!data?.ok) throw new Error(data?.error || "reset_failed");
      message.success({ content: "Счётчики алертов сброшены", key: "settings-reset-alerts" });
    } catch {
      message.error({ content: "Не удалось сбросить счётчики алертов", key: "settings-reset-alerts" });
    }
  }

  return (
    <div style={{ padding: 16 }}>
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <Card size="small" style={{ borderRadius: 14 }}>
          <Space direction="vertical" size={2}>
            <Title level={3} style={{ margin: 0 }}>
              Настройки бота
            </Title>
            <Text type="secondary">
              Управление SLA, антиспамом алертов и базовыми параметрами Telegram-бота.
            </Text>
          </Space>
        </Card>

        {error ? <Alert type="error" showIcon message="Ошибка" description={error} /> : null}

        <Form form={form} layout="vertical" onFinish={saveSettings} initialValues={normalized}>
          <Card
            size="small"
            style={{ borderRadius: 14 }}
            title={
              <Space>
                <BellOutlined />
                Алерты
              </Space>
            }
            extra={
              <Space>
                <Button icon={<ReloadOutlined />} onClick={loadSettings} loading={loading}>
                  Обновить
                </Button>
                <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}>
                  Сохранить
                </Button>
              </Space>
            }
          >
            <Row gutter={[12, 12]}>
              <Col xs={24} md={8}>
                <Form.Item name="alerts_enabled" label="Алерты включены" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="alerts_cooldown_min"
                  label="Cooldown между алертами (мин)"
                  rules={[{ required: true, message: "Укажите cooldown" }]}
                >
                  <InputNumber min={1} max={1440} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="alerts_max_repeats"
                  label="Максимум повторов на кейс"
                  rules={[{ required: true, message: "Укажите лимит повторов" }]}
                >
                  <InputNumber min={1} max={100} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="alerts_digest_enabled" label="Группировать алерты (digest)" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="alerts_digest_interval_min"
                  label="Интервал digest (мин)"
                  rules={[{ required: true, message: "Укажите интервал" }]}
                >
                  <InputNumber min={1} max={1440} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="alerts_group_threshold"
                  label="Порог группировки"
                  rules={[{ required: true, message: "Укажите порог" }]}
                >
                  <InputNumber min={1} max={1000} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="alerts_only_on_status_change" label="Только при изменении статуса" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="alerts_quiet_hours_from" label="Тихие часы — с">
                  <TimePicker format="HH:mm" minuteStep={60} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="alerts_quiet_hours_to" label="Тихие часы — до">
                  <TimePicker format="HH:mm" minuteStep={60} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card
            size="small"
            style={{ borderRadius: 14, marginTop: 12 }}
            title={
              <Space>
                <WarningOutlined />
                SLA
              </Space>
            }
          >
            <Row gutter={[12, 12]}>
              <Col xs={24} md={8}>
                <Form.Item
                  name="sla_default_min"
                  label="SLA по умолчанию (мин)"
                  rules={[{ required: true, message: "Укажите SLA" }]}
                >
                  <InputNumber min={1} max={1440} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card size="small" style={{ borderRadius: 14, marginTop: 12 }} title="Сервисные действия">
            <Space wrap>
              <Button onClick={sendTestAlert}>Тестовый алерт</Button>
              <Button danger onClick={resetAlertCounters}>Сбросить счётчики алертов</Button>
            </Space>

            <Divider style={{ margin: "12px 0" }} />

            <Text type="secondary">
              Текущие значения: cooldown {normalized.alerts_cooldown_min} мин, максимум повторов {normalized.alerts_max_repeats}, digest {normalized.alerts_digest_enabled ? "включён" : "выключен"}, SLA {normalized.sla_default_min} мин.
            </Text>
          </Card>
        </Form>
      </Space>
    </div>
  );
}